import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Permissions } from "@/types/permissions";

export interface CompanyTile {
  id: number;
  default_tile_name: string;
  alias_tile_english: string;
  alias_tile_french: string;
  allowed_to_in_bound: boolean;
  allowed_to_out_bound: boolean;
  status: string;
  created_ts: string;
  updated_ts: string;
  company: number;
  created_by: number | null;
  updated_by: number | null;
  // New creation fields (only for "Lien Search To Do" tile)
  alias_new_creation_english?: string;
  alias_new_creation_french?: string;
  default_new_creation_field_name?: string;
  days_of_records_to_show?: number;
  allowed_for_expiry_report?: boolean;
  // Transition arrays - should contain full transition objects
  to_tiles?: Array<{
    id?: number;
    from_tile: number;
    to_tile: number;
    from_tile_name: string;
    to_tile_name: string;
    comments: "OPTIONAL" | "MANDATORY";
  }>;
  from_tiles?: Array<{
    id?: number;
    from_tile: number;
    to_tile: number;
    from_tile_name: string;
    to_tile_name: string;
    comments: "OPTIONAL" | "MANDATORY";
  }>;
}

export interface TransitionCondition {
  type: "permission" | "organization" | "custom" | "field_validation";
  rule: string;
  value?: any;
  errorMessageKey?: string;
}

export interface ActionConfig {
  id: string;
  type: "button" | "menu_item" | "drag_drop" | "context_menu";
  labelKey: string;
  icon?: string;
  permissions?: Permissions[];
  organizationTypes?: string[];
  confirmationKey?: string;
  handler: string; // Function name to call
}

export interface OrganizationRule {
  organizationType: string[];
  overrides?: Partial<ColumnConfig>;
  disabled?: boolean;
}

export interface ColumnConfig {
  id: string;
  key: string;
  titleKey: string; // i18n key
  descriptionKey?: string;
  color: string;
  order: number;
  maxItems?: number;
  autoRefreshInterval?: number;

  // Permissions
  permissions: {
    view: Permissions[];
    create?: Permissions[];
    edit?: Permissions[];
    delete?: Permissions[];
    dragFrom?: Permissions[];
    dragTo?: Permissions[];
  };

  // Actions available in this column
  actions: ActionConfig[];

  // Organization-specific rules
  organizationRules?: OrganizationRule[];

  // Workflow transitions
  transitions: {
    allowedNext: string[]; // Column IDs that items can move to
    allowedPrev: string[]; // Column IDs that items can come from
    conditions?: TransitionCondition[]; // Additional validation rules
    autoTransition?: {
      trigger: "timer" | "field_change" | "external_event";
      condition: string;
      delay?: number;
    };
  };

  // Display settings
  display: {
    showCount: boolean;
    showCreateButton: boolean;
    expandable: boolean;
    sortBy?: string;
    groupBy?: string;
    filterBy?: string[];
    vin?: boolean;
    opportunityNumber?: boolean;
    amount?: boolean;
    location?: boolean;
    regdoc?: boolean;
    ppsaonvin?: boolean;
    ppsaonowner?: boolean;
    validunitl?: boolean;
    updatedby?: boolean;
    creationDate?: boolean;
  };

  // Custom styling
  styling?: {
    backgroundColor?: string;
    borderColor?: string;
    textColor?: string;
    headerColor?: string;
  };
}

export interface CustomRule {
  id: string;
  name: string;
  description: string;
  condition: string; // JavaScript expression
  action: string;
  priority: number;
  active: boolean;
}

export interface OrganizationConfig {
  organizationType: string;
  customColumns?: Partial<ColumnConfig>[];
  hiddenColumns?: string[];
  customWorkflow?: Record<string, string[]>; // Override transitions
  branding?: {
    colors?: Record<string, string>;
    logo?: string;
    theme?: string;
  };
}

interface DashboardConfigState {
  columns: ColumnConfig[];
  organizationConfigs: Record<string, OrganizationConfig>;
  customRules: CustomRule[];
  activeOrganizationType: string;
  isLoading: boolean;
  lastUpdated: string;
  version: string;
  companyTiles: CompanyTile[];

  // Tab-specific form configurations
  formConfig: {
    isRequired: boolean;

    // Lane heading configurations for each tab
    todoLaneHeadingCustomEn: string;
    todoLaneHeadingCustomFr: string;
    reviewLaneHeadingCustomEn: string;
    reviewLaneHeadingCustomFr: string;
    correctLaneHeadingCustomEn: string;
    correctLaneHeadingCustomFr: string;
    approvedLaneHeadingCustomEn: string;
    approvedLaneHeadingCustomFr: string;
    waiverLaneHeadingCustomEn: string;
    waiverLaneHeadingCustomFr: string;
    successLaneHeadingCustomEn: string;
    successLaneHeadingCustomFr: string;
    cancelledLaneHeadingCustomEn: string;
    cancelledLaneHeadingCustomFr: string;

    todoDefaultTileName: string;
    reviewDefaultTileName: string;
    correctDefaultTileName: string;
    approvedDefaultTileName: string;
    waiverDefaultTileName: string;
    successDefaultTileName: string;
    cancelledDefaultTileName: string;

    // Search configurations (only for todo)
    todoSearchLabelCustomEn: string;
    todoSearchLabelCustomFr: string;
    toddoSearchLabelDefault: string;

    // New creation configurations (only for todo - "Lien Search To Do")
    todoNewCreationLabelEn: string;
    todoNewCreationLabelFr: string;
    todoDefaultNewCreationFieldName: string;

    // Manual move configurations for each tab
    todoManualInbound: boolean | null;
    todoManualOutbound: boolean | null;
    reviewManualInbound: boolean | null;
    reviewManualOutbound: boolean | null;
    correctManualInbound: boolean | null;
    correctManualOutbound: boolean | null;
    approvedManualInbound: boolean | null;
    approvedManualOutbound: boolean | null;
    waiverManualInbound: boolean | null;
    waiverManualOutbound: boolean | null;
    successManualInbound: boolean | null;
    successManualOutbound: boolean | null;
    cancelledManualInbound: boolean | null;
    cancelledManualOutbound: boolean | null;

    // Transition configurations for each tab
    todoInboundTransitions: any[];
    todoOutboundTransitions: any[];
    reviewInboundTransitions: any[];
    reviewOutboundTransitions: any[];
    correctInboundTransitions: any[];
    correctOutboundTransitions: any[];
    approvedInboundTransitions: any[];
    approvedOutboundTransitions: any[];
    waiverInboundTransitions: any[];
    waiverOutboundTransitions: any[];
    successInboundTransitions: any[];
    successOutboundTransitions: any[];
    cancelledInboundTransitions: any[];
    cancelledOutboundTransitions: any[];

    // Form states
    isSaving: boolean;
    hasUnsavedChanges: boolean;
  };
}

// Default column configurations
const DEFAULT_COLUMNS: ColumnConfig[] = [
  {
    id: "lien_search_todo",
    key: "LIEN_SEARCH_TODO",
    titleKey: "lien.lienSearchTodo",
    color: "#3B82F6",
    order: 0,
    permissions: {
      view: [Permissions.LIEN_PROCESS_VIEW],
      create: [Permissions.LIEN_PROCESS_CREATE],
      edit: [Permissions.LIEN_PROCESS_EDIT],
      dragFrom: [Permissions.LIEN_PROCESS_EDIT],
    },
    actions: [
      {
        id: "create_lien",
        type: "button",
        labelKey: "dashboard.newLien",
        icon: "plus",
        permissions: [Permissions.LIEN_PROCESS_CREATE],
        handler: "handleCreateLien",
      },
    ],
    transitions: {
      allowedNext: ["lien_summary_to_review"],
      allowedPrev: [],
      conditions: [
        {
          type: "field_validation",
          rule: "required_fields_completed",
          errorMessageKey: "validation.required_fields_missing",
        },
      ],
    },
    display: {
      showCount: true,
      showCreateButton: true,
      expandable: true,
      sortBy: "created_date",
      vin: true,
      opportunityNumber: true,
      amount: true,
      location: true,
      regdoc: true,
      ppsaonvin: true,
      ppsaonowner: true,
      validunitl: true,
      updatedby: true,
      creationDate: false,
    },
  },
  {
    id: "lien_summary_to_review",
    key: "LIEN_SUMMARY_TO_REVIEW",
    titleKey: "lien.lienSummaryToReview",
    color: "#F59E0B",
    order: 1,
    permissions: {
      view: [Permissions.LIEN_SUMMARY_VIEW],
      edit: [Permissions.LIEN_SUMMARY_EDIT],
      dragFrom: [Permissions.LIEN_SUMMARY_EDIT],
      dragTo: [Permissions.LIEN_SUMMARY_EDIT],
    },
    actions: [
      {
        id: "review_lien",
        type: "menu_item",
        labelKey: "dashboard.review",
        icon: "eye",
        permissions: [Permissions.LIEN_SUMMARY_VIEW],
        handler: "handleReviewLien",
      },
    ],
    transitions: {
      allowedNext: ["lien_summary_to_correct", "lien_summary_approved"],
      allowedPrev: ["lien_search_todo"],
      conditions: [
        {
          type: "permission",
          rule: "can_review",
          errorMessageKey: "error.insufficient_permissions",
        },
      ],
    },
    display: {
      showCount: true,
      showCreateButton: false,
      expandable: true,
    },
  },
  {
    id: "lien_summary_to_correct",
    key: "LIEN_SUMMARY_TO_CORRECT",
    titleKey: "lien.lienSummaryToCorrect",
    color: "#EF4444",
    order: 2,
    permissions: {
      view: [Permissions.LIEN_SUMMARY_VIEW],
      edit: [Permissions.LIEN_SUMMARY_EDIT],
      dragFrom: [Permissions.LIEN_SUMMARY_EDIT],
    },
    actions: [
      {
        id: "correct_lien",
        type: "button",
        labelKey: "dashboard.correct",
        icon: "pencil",
        permissions: [Permissions.LIEN_SUMMARY_EDIT],
        handler: "handleCorrectLien",
      },
    ],
    transitions: {
      allowedNext: ["lien_summary_to_review"],
      allowedPrev: ["lien_summary_to_review"],
    },
    display: {
      showCount: true,
      showCreateButton: false,
      expandable: true,
    },
  },
  {
    id: "lien_summary_approved",
    key: "LIEN_SUMMARY_APPROVED",
    titleKey: "lien.lienSummaryApproved",
    color: "#10B981",
    order: 3,
    permissions: {
      view: [Permissions.LIEN_SUMMARY_VIEW],
      dragFrom: [Permissions.LIEN_DRAG_APPROVE],
      dragTo: [Permissions.LIEN_DRAG_APPROVE],
    },
    actions: [
      {
        id: "generate_waiver",
        type: "menu_item",
        labelKey: "dashboard.generateWaiver",
        icon: "document",
        permissions: [Permissions.LIEN_SUMMARY_GENERATE_WAIVER],
        handler: "handleGenerateWaiver",
      },
    ],
    transitions: {
      allowedNext: ["waiver_payout_request"],
      allowedPrev: ["lien_summary_to_review"],
      conditions: [
        {
          type: "permission",
          rule: "can_approve",
          errorMessageKey: "error.cannot_approve",
        },
      ],
    },
    display: {
      showCount: true,
      showCreateButton: false,
      expandable: true,
    },
  },
  {
    id: "waiver_payout_request",
    key: "WAIVER_PAYOUT_REQUEST",
    titleKey: "lien.waiverPayoutRequest",
    color: "#8B5CF6",
    order: 4,
    permissions: {
      view: [Permissions.LIEN_SUMMARY_VIEW],
      edit: [Permissions.LIEN_SUMMARY_EDIT_WAIVER],
      dragFrom: [Permissions.LIEN_DRAG_WAIVER],
    },
    actions: [
      {
        id: "process_waiver",
        type: "button",
        labelKey: "dashboard.processWaiver",
        icon: "check",
        permissions: [Permissions.LIEN_DRAG_WAIVER],
        confirmationKey: "confirmation.processWaiver",
        handler: "handleProcessWaiver",
      },
    ],
    transitions: {
      allowedNext: ["successfully_closed", "deal_cancelled"],
      allowedPrev: ["lien_summary_approved"],
    },
    display: {
      showCount: true,
      showCreateButton: false,
      expandable: true,
    },
  },
  {
    id: "successfully_closed",
    key: "SUCCESSFULLY_CLOSED",
    titleKey: "lien.successfullyClosed",
    color: "#059669",
    order: 5,
    permissions: {
      view: [Permissions.LIEN_SUMMARY_VIEW],
    },
    actions: [
      {
        id: "view_summary",
        type: "menu_item",
        labelKey: "dashboard.viewSummary",
        icon: "document-text",
        permissions: [Permissions.LIEN_SUMMARY_VIEW],
        handler: "handleViewSummary",
      },
    ],
    transitions: {
      allowedNext: [],
      allowedPrev: ["waiver_payout_request"],
    },
    display: {
      showCount: true,
      showCreateButton: false,
      expandable: false,
    },
  },
  {
    id: "deal_cancelled",
    key: "DEAL_CANCELLED",
    titleKey: "lien.dealCancelled",
    color: "#DC2626",
    order: 6,
    permissions: {
      view: [Permissions.LIEN_SUMMARY_VIEW],
    },
    actions: [],
    transitions: {
      allowedNext: [],
      allowedPrev: ["waiver_payout_request"],
    },
    display: {
      showCount: true,
      showCreateButton: false,
      expandable: false,
    },
  },
];

const initialState: DashboardConfigState = {
  columns: DEFAULT_COLUMNS,
  organizationConfigs: {},
  customRules: [],
  activeOrganizationType: "default",
  isLoading: false,
  lastUpdated: new Date().toISOString(),
  version: "1.0.0",
  companyTiles: [],
  formConfig: {
    isRequired: false,

    // Lane headings
    todoLaneHeadingCustomEn: "",
    todoLaneHeadingCustomFr: "",
    reviewLaneHeadingCustomEn: "",
    reviewLaneHeadingCustomFr: "",
    correctLaneHeadingCustomEn: "",
    correctLaneHeadingCustomFr: "",
    approvedLaneHeadingCustomEn: "",
    approvedLaneHeadingCustomFr: "",
    waiverLaneHeadingCustomEn: "",
    waiverLaneHeadingCustomFr: "",
    successLaneHeadingCustomEn: "",
    successLaneHeadingCustomFr: "",
    cancelledLaneHeadingCustomEn: "",
    cancelledLaneHeadingCustomFr: "",

    todoDefaultTileName: "",
    reviewDefaultTileName: "",
    correctDefaultTileName: "",
    approvedDefaultTileName: "",
    waiverDefaultTileName: "",
    successDefaultTileName: "",
    cancelledDefaultTileName: "",

    // Search
    todoSearchLabelCustomEn: "",
    todoSearchLabelCustomFr: "",
    toddoSearchLabelDefault: "New Search",

    // New creation configurations (only for todo - "Lien Search To Do")
    todoNewCreationLabelEn: "",
    todoNewCreationLabelFr: "",
    todoDefaultNewCreationFieldName: "",

    // Manual move settings
    todoManualInbound: null,
    todoManualOutbound: null,
    reviewManualInbound: null,
    reviewManualOutbound: null,
    correctManualInbound: null,
    correctManualOutbound: null,
    approvedManualInbound: null,
    approvedManualOutbound: null,
    waiverManualInbound: null,
    waiverManualOutbound: null,
    successManualInbound: null,
    successManualOutbound: null,
    cancelledManualInbound: null,
    cancelledManualOutbound: null,

    // Transitions
    todoInboundTransitions: [],
    todoOutboundTransitions: [],
    reviewInboundTransitions: [],
    reviewOutboundTransitions: [],
    correctInboundTransitions: [],
    correctOutboundTransitions: [],
    approvedInboundTransitions: [],
    approvedOutboundTransitions: [],
    waiverInboundTransitions: [],
    waiverOutboundTransitions: [],
    successInboundTransitions: [],
    successOutboundTransitions: [],
    cancelledInboundTransitions: [],
    cancelledOutboundTransitions: [],

    // Form states
    isSaving: false,
    hasUnsavedChanges: false,
  },
};

const dashboardConfigSlice = createSlice({
  name: "dashboardConfig",
  initialState,
  reducers: {
    // Column management
    updateColumnConfig: (
      state,
      action: PayloadAction<{
        columnId: string;
        config: Partial<ColumnConfig>;
      }>,
    ) => {
      const { columnId, config } = action.payload;
      const columnIndex = state.columns.findIndex((col) => col.id === columnId);
      if (columnIndex !== -1) {
        state.columns[columnIndex] = {
          ...state.columns[columnIndex],
          ...config,
        };
        state.lastUpdated = new Date().toISOString();
      }
    },

    addColumn: (state, action: PayloadAction<ColumnConfig>) => {
      state.columns.push(action.payload);
      state.columns.sort((a, b) => a.order - b.order);
      state.lastUpdated = new Date().toISOString();
    },

    removeColumn: (state, action: PayloadAction<string>) => {
      state.columns = state.columns.filter((col) => col.id !== action.payload);
      state.lastUpdated = new Date().toISOString();
    },

    reorderColumns: (
      state,
      action: PayloadAction<{ fromIndex: number; toIndex: number }>,
    ) => {
      const { fromIndex, toIndex } = action.payload;
      const [movedColumn] = state.columns.splice(fromIndex, 1);
      state.columns.splice(toIndex, 0, movedColumn);

      // Update order values
      state.columns.forEach((col, index) => {
        col.order = index;
      });
      state.lastUpdated = new Date().toISOString();
    },

    // Organization configuration
    updateOrganizationConfig: (
      state,
      action: PayloadAction<{ orgType: string; config: OrganizationConfig }>,
    ) => {
      const { orgType, config } = action.payload;
      state.organizationConfigs[orgType] = config;
      state.lastUpdated = new Date().toISOString();
    },

    setActiveOrganizationType: (state, action: PayloadAction<string>) => {
      state.activeOrganizationType = action.payload;
    },

    // Custom rules
    addCustomRule: (state, action: PayloadAction<CustomRule>) => {
      state.customRules.push(action.payload);
      state.lastUpdated = new Date().toISOString();
    },

    updateCustomRule: (
      state,
      action: PayloadAction<{ ruleId: string; rule: Partial<CustomRule> }>,
    ) => {
      const { ruleId, rule } = action.payload;
      const ruleIndex = state.customRules.findIndex((r) => r.id === ruleId);
      if (ruleIndex !== -1) {
        state.customRules[ruleIndex] = {
          ...state.customRules[ruleIndex],
          ...rule,
        };
        state.lastUpdated = new Date().toISOString();
      }
    },

    removeCustomRule: (state, action: PayloadAction<string>) => {
      state.customRules = state.customRules.filter(
        (rule) => rule.id !== action.payload,
      );
      state.lastUpdated = new Date().toISOString();
    },

    // Configuration loading
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },

    // Bulk configuration update
    loadConfiguration: (
      state,
      action: PayloadAction<Partial<DashboardConfigState>>,
    ) => {
      return {
        ...state,
        ...action.payload,
        lastUpdated: new Date().toISOString(),
      };
    },

    resetToDefaults: (state) => {
      state.columns = DEFAULT_COLUMNS;
      state.organizationConfigs = {};
      state.customRules = [];
      state.lastUpdated = new Date().toISOString();
    },

    // Form configuration management
    updateFormConfig: (
      state,
      action: PayloadAction<Partial<DashboardConfigState["formConfig"]>>,
    ) => {
      if (!state.formConfig) {
        state.formConfig = initialState.formConfig;
      }
      state.formConfig = { ...state.formConfig, ...action.payload };
      state.formConfig.hasUnsavedChanges = true;
      state.lastUpdated = new Date().toISOString();
    },

    updateFormField: (
      state,
      action: PayloadAction<{
        field: keyof DashboardConfigState["formConfig"];
        value: any;
      }>,
    ) => {
      const { field, value } = action.payload;
      if (!state.formConfig) {
        state.formConfig = initialState.formConfig;
      }
      (state.formConfig as any)[field] = value;
      state.formConfig.hasUnsavedChanges = true;
      state.lastUpdated = new Date().toISOString();
    },

    saveFormConfigStart: (state) => {
      if (!state.formConfig) {
        state.formConfig = initialState.formConfig;
      }
      state.formConfig.isSaving = true;
    },

    saveFormConfigSuccess: (state) => {
      if (!state.formConfig) {
        state.formConfig = initialState.formConfig;
      }
      state.formConfig.isSaving = false;
      state.formConfig.hasUnsavedChanges = false;
      state.lastUpdated = new Date().toISOString();
    },

    saveFormConfigFailure: (state) => {
      if (!state.formConfig) {
        state.formConfig = initialState.formConfig;
      }
      state.formConfig.isSaving = false;
    },

    resetFormConfig: (state) => {
      state.formConfig = initialState.formConfig;
    },

    loadConfigStart: (state) => {
      state.isLoading = true;
    },

    loadConfigSuccess: (
      state,
      action: PayloadAction<{ companyTiles: CompanyTile[]; formConfig?: any }>,
    ) => {
      state.isLoading = false;
      state.companyTiles = action.payload.companyTiles;
      if (action.payload.formConfig) {
        state.formConfig = {
          ...state.formConfig,
          ...action.payload.formConfig,
        };
      }
      state.lastUpdated = new Date().toISOString();
    },

    loadConfigFailure: (state) => {
      state.isLoading = false;
    },

    loadFromStorage: (state) => {
      // This will be handled by the documentSync.ts to load from localStorage
      // The actual data loading is done there and then dispatched via loadConfigSuccess
      console.log("Loading company tiles from localStorage");
    },
  },
});

export const {
  updateColumnConfig,
  addColumn,
  removeColumn,
  reorderColumns,
  updateOrganizationConfig,
  setActiveOrganizationType,
  addCustomRule,
  updateCustomRule,
  removeCustomRule,
  setLoading,
  loadConfiguration,
  resetToDefaults,
  updateFormConfig,
  updateFormField,
  saveFormConfigStart,
  saveFormConfigSuccess,
  saveFormConfigFailure,
  resetFormConfig,
  loadConfigStart,
  loadConfigSuccess,
  loadConfigFailure,
  loadFromStorage,
} = dashboardConfigSlice.actions;

export default dashboardConfigSlice.reducer;
